/**
 * A URL written the way it used to be, in the shape React Router matches now.
 *
 * Two generations of old link arrive here. Angular put route parameters into
 * the path segment itself — `/quoting/quotes;quoteId=12;revisionId=3` — and
 * React Router reads that as a segment named `quotes;quoteId=12;revisionId=3`,
 * which matches no route at all. The React screens that replaced it moved
 * those parameters, and the open tab or report of a dozen other screens, into
 * the query string. Both are path segments now.
 *
 * `main.tsx` runs every incoming URL through this before the router sees it,
 * so a bookmark or a link from another system written in either old shape
 * still opens what it named.
 */
export function normaliseHostPath(url: string): string {
  const [rawPath, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  const path = foldMatrixParams(rawPath, params);
  const rewritten = rewriteLegacyPath(path, params);
  const search = params.toString();
  return search ? `${rewritten}?${search}` : rewritten;
}

/**
 * Angular's matrix parameters, moved into the query string where
 * `URLSearchParams` can read them. A query parameter of the same name is the
 * caller's own and wins.
 */
function foldMatrixParams(path: string, params: URLSearchParams): string {
  return path
    .split('/')
    .map((segment) => {
      const [name, ...matrix] = segment.split(';');
      for (const pair of matrix) {
        if (!pair) continue;
        const separator = pair.indexOf('=');
        const key = separator === -1 ? pair : pair.slice(0, separator);
        const value = separator === -1 ? '' : pair.slice(separator + 1);
        if (key && !params.has(key)) {
          params.set(key, safeDecode(value));
        }
      }
      return name;
    })
    .join('/');
}

/** A stray `%` in a matrix value would otherwise throw and lose the whole URL. */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Every key an old link carries, against the segment it means today. */
type Vocabulary = Record<string, string>;

/** Keys that were already written the way a URL is, and did not have to move. */
function unchanged(...keys: string[]): Vocabulary {
  return Object.fromEntries(keys.map((key) => [key, key]));
}

/**
 * Screens whose open tab or report was a query parameter, and the name that
 * parameter went by.
 *
 * The values are the ones old links carry, which is not always the segment
 * they map to: several of these keys were camelCase, and a URL segment is not.
 * This table is frozen — it says what an old link *meant*, so it must not be
 * kept in step with the live lists. Renaming a report there does not rename it
 * here; the old link still means the report it named at the time.
 */
const LEGACY_SEGMENT_ROUTES: { path: string; param: string; vocabulary: Vocabulary }[] = [
  {
    path: '/sales/reports',
    param: 'report',
    vocabulary: unchanged('projects', 'quote-orders'),
  },
  {
    path: '/sales/enquiries/reports',
    param: 'report',
    vocabulary: unchanged(
      'sales-admin',
      'customer-profile',
      'enquiries-status',
      'quotes-by-estimator',
    ),
  },
  {
    path: '/accounting/reports',
    param: 'report',
    vocabulary: {
      projectsToBeInvoiced: 'to-be-invoiced',
      purchaseOrders: 'purchase-orders',
      backCharges: 'back-charges',
      projectCosts: 'project-costs',
      completedPaid: 'completed-paid',
    },
  },
  {
    path: '/contracts/reports',
    param: 'report',
    vocabulary: {
      installerReport: 'installer',
      projectPlantReport: 'project-plant',
      projectPlantTracker: 'project-plant-tracker',
      contractsAdminProjects: 'contracts-admin-projects',
      projectAcknowledgementsReport: 'project-acknowledgements',
      projectsNcrsReport: 'projects-ncrs',
    },
  },
  {
    path: '/production/reports',
    param: 'report',
    vocabulary: { weeklyDetailing: 'weekly-detailing' },
  },
  {
    path: '/production/orders',
    param: 'tab',
    vocabulary: unchanged('outstanding', 'approved', 'archived'),
  },
  {
    path: '/production/stock-control',
    param: 'view',
    vocabulary: {
      projects: 'projects',
      stockOrders: 'stock-orders',
      updateStockItems: 'stock-items',
      fabricatorStockReport: 'fabricator-report',
      backCharges: 'back-charges',
    },
  },
  {
    path: '/data-management/online-users',
    param: 'tab',
    vocabulary: unchanged('now', 'sessions', 'daily', 'companies'),
  },
  {
    // The year and the grouping stay in the query: they are how the report is
    // being read rather than which report it is.
    path: '/reports/project-savings',
    param: 'tab',
    vocabulary: unchanged('monthly', 'summary'),
  },
];

/**
 * The project view's tabs, which were keyed by the tab registry's own name
 * rather than by anything URL-shaped.
 */
const LEGACY_PROJECT_TABS: Vocabulary = {
  ...unchanged(
    'main',
    'chat',
    'docs',
    'ncrs',
    'variations',
    'quote',
    'design',
    'drawing',
    'order',
    'invoices',
  ),
  engCalc: 'eng-calc',
  methodStatement: 'method-statement',
  checkCost: 'check-cost',
  purchaseOrder: 'purchase-order',
};

const PROJECT_VIEW = /^\/projects\/(\d+)\/view$/;

/**
 * Screens that opened one record beside a rail, and named it in the query.
 *
 * Unlike the tab and report screens above there is no vocabulary to translate:
 * the value was the record's id then and is its segment now.
 */
const LEGACY_RECORD_ROUTES: { path: string; param: string }[] = [
  { path: '/accounting/purchase-orders', param: 'order' },
  { path: '/accounting/order-acknowledgements', param: 'acknowledgement' },
];

/** A positive whole number, or null — an id that is neither names nothing. */
function idParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  return value !== null && /^\d+$/.test(value) && Number(value) > 0 ? value : null;
}

/**
 * The old address of a screen, rewritten as the path it lives at now. The
 * parameters that became segments are taken out of the query; anything else
 * the link carried is left where it is.
 *
 * Only the exact old path is matched, so a URL already in the new shape passes
 * through untouched.
 */
function rewriteLegacyPath(path: string, params: URLSearchParams): string {
  const projectView = PROJECT_VIEW.exec(path);
  if (projectView) {
    const tab = LEGACY_PROJECT_TABS[params.get('tab') ?? ''];
    if (!tab) return path;
    params.delete('tab');
    return `/projects/${projectView[1]}/view/${tab}`;
  }

  if (path === '/quoting/quotes') {
    const quoteId = idParam(params, 'quoteId');
    // A revision means nothing without the quote it is a revision of.
    if (!quoteId) return path;
    const revisionId = idParam(params, 'revisionId');
    params.delete('quoteId');
    params.delete('revisionId');
    return revisionId
      ? `/quoting/quotes/${quoteId}/revision/${revisionId}`
      : `/quoting/quotes/${quoteId}`;
  }

  if (path === '/quoting/quote') {
    // The estimator moved under the quotes drill-down it is the last step of.
    // All three ids are needed: the session opens on the option, under the
    // revision, under the quote. A link short of any of them named no quote to
    // price, and lands on the list.
    const quoteId = idParam(params, 'quote_id');
    const revisionId = idParam(params, 'revision_id');
    const optionId = idParam(params, 'option_id');
    if (!quoteId || !revisionId || !optionId) return path;
    params.delete('quote_id');
    params.delete('revision_id');
    params.delete('option_id');
    return `/quoting/quotes/${quoteId}/revision/${revisionId}/option/${optionId}`;
  }

  if (path === '/quoting/quote-document') {
    // A document is a revision's, and is addressed by it alone. A link that
    // also carried the quote loses it: the screen asks which quote the revision
    // is under, so nothing needs it in the URL.
    const revisionId = idParam(params, 'revisionId');
    if (!revisionId) return path;
    params.delete('quoteId');
    params.delete('revisionId');
    return `/quoting/quote-document/revision/${revisionId}`;
  }

  for (const route of LEGACY_RECORD_ROUTES) {
    if (path !== route.path) continue;
    const id = idParam(params, route.param);
    if (!id) return path;
    params.delete(route.param);
    return `${route.path}/${id}`;
  }

  for (const route of LEGACY_SEGMENT_ROUTES) {
    if (path !== route.path) continue;
    const segment = route.vocabulary[params.get(route.param) ?? ''];
    if (!segment) return path;
    params.delete(route.param);
    return `${route.path}/${segment}`;
  }

  return path;
}
